import React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import { mediaQueryUp } from 'components/utils/breakpoint'
import Image from 'components/Common/Image'
import TextButton from 'components/Luxkit/Button/TextButton'
import OfferBadgeTag from 'components/Common/OfferBadgeTag'
import LineMapMarkerIcon from 'components/Luxkit/Icons/line/LineMapMarkerIcon'
import OfferDescription from '../OfferDescription'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import SolidPlayIcon from 'components/Luxkit/Icons/solid/SolidPlayIcon'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import OfferPagePropertyTitle from '../OfferPagePropertyTitle'
import Subtitle from 'components/Luxkit/Typography/Subtitle'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import PropertyMap from 'components/Common/PropertyMap'
import { OFFER_SUMMARY_MAP_HEIGHT, PROPERTY_MAP_MARKER_TYPE } from 'constants/map'
import OfferPageIntroInclusions from '../OfferPageIntroInclusions'
import { OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import OfferPromotions from '../OfferPromotions'
import Divider from 'components/Luxkit/Divider'

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const PropertyLogo = styled(Image)`
  display: none;

  ${mediaQueryUp.desktop} {
    margin-left: ${rem(16)};
    display: block;
    height: ${rem(75)};
  }
`

const OfferBadgeTagSection = styled.div`
  > * + * {
    margin-left: ${rem(8)};
  }
`

const ContentColumn = styled.div`
  > * + * {
    margin-top: ${rem(16)};
  }
`

interface Props {
  offer: App.Offer;
  property: App.OfferProperty;
  location: string;
  onWatchVideo: () => void;
  isMapEnabled: boolean;
  promotions: Array<OfferPackageOptionView>;
}

function UltraLuxPropertyDetailsExperimentVariant(props: Props) {
  const {
    offer,
    property,
    onWatchVideo,
    location,
    isMapEnabled,
    promotions,
  } = props

  return (<ContentColumn>
    <Header>
      <VerticalSpacer gap={8}>
        <BodyTextBlock variant="medium" data-testid="offer-location" startIcon={<LineMapMarkerIcon />}>
          {location}
        </BodyTextBlock>
        <OfferPagePropertyTitle propertyName={property.name} />
        <Subtitle variant="subtitle2" as="h2">{offer.name}</Subtitle>
      </VerticalSpacer>
      {property.logoImageId &&
        <PropertyLogo
          dpr={2}
          height={75}
          enhancedFormats={false}
          id={property.logoImageId}
          alt={`${property.name || 'Vendor'} logo`}
        />}
    </Header>
    {(!!offer.badge || offer.bundledWithFlightsOnly) && <OfferBadgeTagSection>
      {offer.badge && <OfferBadgeTag badge={offer.badge} />}
    </OfferBadgeTagSection>}

    {offer.vimeoVideoId &&
      <TextButton kind="secondary" startIcon={<SolidPlayIcon />} onClick={onWatchVideo}>
        Play video
      </TextButton>
    }
    {offer.tileInclusions && offer.tileInclusions.length > 2 && <>
      <OfferPageIntroInclusions tileInclusionsList={offer.tileInclusions} heading="Your handpicked inclusions:" />
      <Divider kind="primary" />
    </>}
    {offer.description && <OfferDescription
      description={offer.description}
      additionalDescription={offer.additionalDescription}
      offer={offer}
      offerPageTruncateText
    />}
    <CSSBreakpoint max="mobile">
      {isMapEnabled && property && <PropertyMap
        height={OFFER_SUMMARY_MAP_HEIGHT}
        wrapperStyle={{ height: OFFER_SUMMARY_MAP_HEIGHT }}
        markers={[{ id: property.id, type: PROPERTY_MAP_MARKER_TYPE, latitude: property.latitude, longitude: property.longitude }]}
      />}
    </CSSBreakpoint>
    {promotions.length > 0 && <OfferPromotions offer={offer} promotions={promotions} />}
  </ContentColumn>
  )
}

export default UltraLuxPropertyDetailsExperimentVariant
